import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { Updater, useImmer } from 'use-immer';
import {
  EClaimStages,
  EClaimType,
  ICategory,
  IClaimDetails,
  IDocumentsRequiredContent,
  ISection,
  TCheckbox,
} from '@/types';
import { IFormStatuses } from '@/hooks/useFormStatuses';
import { useMutation, UseQueryResult } from '@tanstack/react-query';
import useClaimDetails from '@/hooks/useClaimDetails';
import useConfirm from '@/hooks/useConfirm';
import {
  claimDetailsQuery,
  deleteClaimDocument,
  generateSignedUrl,
} from '@/queries/claims.queries';
import toast from 'react-hot-toast';
import uploadFile from '@/utils/uploadFile';
import { queryClient } from './CombinedProvider';
import useDuration, { Timers } from '@/hooks/useDuration';
import { useAppContext } from './AppContext';
import { FEATURE_FLAGS } from '@/constants';
import { redirectToPlumApp } from '@/utils';

interface IProps {
  claimDetails?: IClaimDetails;
  autoRedirectToUpload?: boolean;
  autoRedirectToStatus?: boolean;
}

interface IActiveSteps {
  steps: Array<IDocumentsRequiredContent>;
  currentStep: IDocumentsRequiredContent;
  currentStepIndex: number;
  currentCategory: ICategory;
  currentCategoryIndex: number;
  currentSection: ISection;
  currentSectionIndex: number;
  isClaimDigitised: boolean;
  claimId: string;
  isClaimPreDigitised: boolean | null;
  digitisationSuccessful: boolean;
  typeOfCare: string;
  insuranceProviderHandle: string;
  tpaName: string;
  typeOfBenefit: string;
  typeOfClaim: string;
  currentDisplayStepIndex: number;
  totalDisplaySteps: number;
}

/**
 * Function to get the next valid step in claims upload flow
 * @param claimDetails
 * @returns a redirection url object to be consumed by router, whether the current route is allowed or not and a list of allowed urls
 */
const getLastAllowedUrl = (
  claimDetails: IClaimDetails,
  currentStepSlug: string | null,
): {
  isAllowed: boolean;
  redirectTo: string;
  allowedUrls: Array<string>;
} => {
  const {
    plumClaimId: claimId,
    documentsUploadPageContent: steps = [],
    nextAllowedStepSlug,
  } = claimDetails ?? {};

  const currentStepIndex = steps.findIndex(
    (stp) => stp.slug === currentStepSlug,
  );
  const nextStepIndex = steps.findIndex(
    (step) => step.slug === nextAllowedStepSlug,
  );
  // can be false if the claim is just created or completely submitted
  const isNextSlugInSteps = nextStepIndex !== -1;

  const isFinalStep = currentStepIndex + 1 === steps?.length;

  const isAllowed =
    currentStepIndex != -1 &&
    // the next allowed step is in the array and current step is lesser than that
    ((isNextSlugInSteps && currentStepIndex <= nextStepIndex) ||
      // the next allowed step is null which means claim is just created and current step is the first one
      (nextAllowedStepSlug == null && currentStepIndex === 0) ||
      // for handling claims which are just now submitted and showing the animation screen
      (isFinalStep && nextAllowedStepSlug === '/'));

  let redirectTo = `/claims/${claimId}/upload`;

  if (nextAllowedStepSlug != null && isNextSlugInSteps) {
    redirectTo = `/claims/${claimId}/upload/${nextAllowedStepSlug}`;
  } else if (steps[0] != null) {
    redirectTo = `/claims/${claimId}/upload/${steps[0]?.slug}`;
  }

  // only allow the steps which are before the next allowed step
  // first step is always allowed
  const allowedUrls =
    steps
      ?.filter((_, idx) => idx <= nextStepIndex || idx === 0)
      ?.map((step) => `/claims/${claimId}/upload/${step.slug}`) ?? [];

  return { isAllowed, redirectTo, allowedUrls };
};

export interface ICWTrackingStats {
  skippedSections: Array<string>;
  sectionsWithFrontendErrors: Array<string>;
  totalFrontendErrors: number;
  sectionsWithErrors: Array<string>;
  totalVerificationErrors: number;
  totalUnverified: number;
  currentStepSlug: string;
  currentStepNumber: number;
  nextStepSlug: string;
  nextStepNumber: number | null;
  totalSteps: number | null;
  currentCategorySlug: string;
  currentSectionTag: string;
  claimId: string;
  isFinalStep: boolean;
  digitisationSuccessful: boolean;
  isClaimPreDigitised: boolean | null;
  typeOfCare: string;
  insuranceProviderHandle: string;
  tpaName: string;
  typeOfBenefit: string;
  typeOfClaim: string;
  prefillOfferedSections: Array<string>;
  prefillAdoptedSections: Array<string>;
  prefillEditableSectionTitles: Array<string>;
  timeSpent?: Array<{ step: string; duration: number }>;
}

const getTrackingStats = (
  activeSteps: IActiveSteps,
  durationRef: React.MutableRefObject<Timers>,
) => {
  const {
    currentStepIndex,
    currentStep,
    currentCategory,
    currentSection,
    steps,
    claimId,
    digitisationSuccessful,
    isClaimPreDigitised,
    tpaName,
    insuranceProviderHandle,
    typeOfCare,
    typeOfBenefit,
    typeOfClaim,
  } = activeSteps;
  // adding 1 for treatment details form step
  const totalSteps = steps?.length > 0 ? steps?.length + 1 : null;
  // adding 1 for treatment details form step and 1 more for converting index to number
  const currentStepNumber = 1 + currentStepIndex + 1;
  const isFinalStep = currentStepNumber === totalSteps;
  const nextStepIndex = isFinalStep ? null : currentStepIndex + 1;
  // adding 1 for treatment details form step and 1 more for converting index to number
  const nextStepNumber = isFinalStep ? null : 1 + (nextStepIndex ?? 0) + 1;
  return (
    allFormStatus: IFormStatuses,
    currentFormStatus: IFormStatuses,
  ): ICWTrackingStats => {
    let totalFrontendErrors = 0;
    const sectionsWithFrontendErrors = Array.from(
      currentFormStatus.section,
    ).filter(([, status]) => {
      const frontEndErrors =
        (status?.count ?? 0) - (status?.verificationCount ?? 0);
      totalFrontendErrors += frontEndErrors;
      return frontEndErrors > 0;
    });

    let totalVerificationErrors = 0;
    let totalUnverified = 0;
    Array.from(allFormStatus.step).forEach(
      ([, { verificationCount, unverifiedCount }]) => {
        totalVerificationErrors += verificationCount ?? 0;
        totalUnverified += unverifiedCount ?? 0;
      },
    );

    const prefillOfferedSections: Array<string> = [];
    const prefillAdoptedSections: Array<string> = [];
    const prefillEditableSectionTitles: Array<string> = [];

    steps?.forEach?.((currStep) => {
      const { categories = [], inputFieldGroups } = currStep ?? {};

      inputFieldGroups?.map?.((fieldGroup) => {
        const {
          CTA: { type },
          title,
        } = fieldGroup;

        if (type === 'inline-edit') {
          prefillEditableSectionTitles.push(title);
        }
      });

      categories?.forEach?.((currCategory) => {
        const { sections = [] } = currCategory ?? {};

        sections?.forEach?.((currSection) => {
          const {
            sectionTag,
            preFillBanner = null,
            dependentPreFillBanner = null,
          } = currSection ?? {};

          // pre-fill kyc option was offered for self
          if (preFillBanner != null) {
            prefillOfferedSections.push(sectionTag);
            if (preFillBanner?.state === 'post-filled') {
              prefillAdoptedSections.push(sectionTag);
            }
          }

          // pre-fill kyc option was offered for self
          if (dependentPreFillBanner != null) {
            prefillOfferedSections.push(`${sectionTag}-dependent`);
            if (preFillBanner?.state === 'post-filled') {
              prefillAdoptedSections.push(`${sectionTag}-dependent`);
            }
          }
        });
      });
    });

    const trackingStats: ICWTrackingStats = {
      skippedSections: Array.from(allFormStatus?.section)
        .filter(([, status]) => {
          return status.skipped;
        })
        .map(([sectionTag]) => {
          return sectionTag;
        }),
      sectionsWithFrontendErrors: sectionsWithFrontendErrors.map(
        ([sectionTag]) => {
          return sectionTag;
        },
      ),
      totalFrontendErrors,
      sectionsWithErrors: Array.from(allFormStatus?.section)
        .filter(([, status]) => {
          return (
            status.hasToBeVerified &&
            (status.verificationCount ?? 0) > 0 &&
            status.status === 'errors'
          );
        })
        .map(([sectionTag]) => {
          return sectionTag;
        }),
      totalVerificationErrors,
      totalUnverified,
      currentStepSlug: currentStep?.slug,
      currentStepNumber,
      nextStepSlug: steps?.[nextStepIndex ?? 0]?.slug ?? null,
      nextStepNumber,
      currentCategorySlug: currentCategory?.slug ?? null,
      currentSectionTag: currentSection?.sectionTag ?? null,
      totalSteps,
      claimId,
      isFinalStep,
      digitisationSuccessful,
      isClaimPreDigitised,
      typeOfCare,
      insuranceProviderHandle,
      tpaName,
      typeOfBenefit,
      typeOfClaim,
      prefillOfferedSections,
      prefillAdoptedSections,
      prefillEditableSectionTitles,
      timeSpent: Object.keys(durationRef.current).map((step) => ({
        step,
        duration: durationRef.current[step][2] ?? 0,
      })),
    };
    return trackingStats;
  };
};

export interface IClaimsPageState {
  fetchCounter: number;
  lockHubs: boolean;
  forceCloseFPM: boolean;
  isSubmitting: boolean;
  isSubmitted: boolean;
  isSubmitAttempted: boolean;
  isFormValidating: boolean;
  declarations: Array<TCheckbox> | null;
  declarationError: boolean;
  isSigned: boolean;
  signError: boolean;
  isRouteLoading: boolean;
  editInProgressRefs: Map<string, HTMLElement>;
  uploadProgress: Map<string, number>;
  resetCounter: number;
}

interface IClaimUploadContext {
  state: IClaimsPageState;
  setState: Updater<IClaimsPageState>;
  activeSteps: IActiveSteps;
  getTrackingStats: (
    allFormStatus: IFormStatuses,
    currentFormStatus: IFormStatuses,
  ) => ICWTrackingStats;
  getLastAllowedUrl: (
    claimDetails: IClaimDetails,
    currentStepSlug: string,
  ) => {
    isAllowed: boolean;
    redirectTo: string;
  };
  claimDetails?: IClaimDetails | null;
  claimDetailsQueryResult: UseQueryResult<IClaimDetails, Error>;
  onFileDelete: (fileId: string) => void;
  onFileUpload: (
    files: Array<File>,
    fileAttrs: { attrs: Record<string, unknown>; sectionTag: string },
  ) => void;
  durationRef: React.MutableRefObject<Timers>;
}

const ClaimUploadContext = createContext<IClaimUploadContext | null>(null);

/**
 * Provider for the claims upload flow (/upload/:step pages)
 */
export const ClaimUploadProvider = ({
  children,
  autoRedirectToUpload,
  autoRedirectToStatus,
}: React.PropsWithChildren<IProps>) => {
  const { step, category, section, claimId } = useParams();

  const { claimDetails, claimDetailsQueryResult } = useClaimDetails({
    queryOptions: {
      returnDocumentsUploadPageContent: 3,
    },
  });

  const { userConfig, isAppLoading } = useAppContext();

  const [state, setState] = useImmer<IClaimsPageState>({
    fetchCounter: 0,
    lockHubs: false,
    forceCloseFPM: false,
    isSubmitting: false,
    isSubmitted: false,
    isSubmitAttempted: false,
    isFormValidating: false,
    declarations: null,
    declarationError: false,
    isSigned: false,
    signError: false,
    isRouteLoading: true,
    editInProgressRefs: new Map(),
    uploadProgress: new Map<string, number>(),
    resetCounter: 0,
  });

  const { startTimer, pauseTimer, durationRef } = useDuration();

  // @ts-ignore
  const activeSteps = useMemo<IActiveSteps>(() => {
    const steps = claimDetails?.documentsUploadPageContent ?? [];
    const currentStepIndex = steps?.findIndex((STEP) => STEP.slug === step);
    const currentStep = steps?.[currentStepIndex ?? 0] ?? null;

    const currentCategoryIndex = currentStep?.categories?.findIndex(
      ({ slug: categorySlug }) => categorySlug === category,
    );
    const currentCategory =
      currentStep?.categories?.[currentCategoryIndex ?? 0];

    let currentSection = currentCategory?.sections?.[0];
    let currentSectionIndex = 0;
    if ((currentCategory?.sections?.length ?? 0) > 1) {
      currentSectionIndex =
        currentCategory?.sections?.findIndex(
          (sec) => sec.sectionTag === section,
        ) ?? 0;
      currentSection = currentCategory?.sections?.[currentSectionIndex];
    }

    let isClaimDigitised = false;
    if (currentCategoryIndex !== -1) {
      isClaimDigitised = (currentCategory?.inputFieldGroups?.length ?? 0) > 0;
    } else {
      isClaimDigitised = (currentStep?.inputFieldGroups?.length ?? 0) > 0;
    }

    const {
      plumClaimId: claimId = '',
      isClaimPreDigitised = null,
      digitisationSuccessful,
      typeOfCare,
      insuranceProviderHandle,
      tpaName,
      typeOfBenefit,
      typeOfClaim,
    } = claimDetails ?? {};

    const displaySteps =
      steps?.filter(
        (step) =>
          step?.displayAsNonStep == null || step?.displayAsNonStep === false,
      ) ?? [];

    const totalDisplaySteps = displaySteps?.length ?? 0;

    const currentDisplayStepIndex = displaySteps?.findIndex(
      (step) => step.slug === currentStep?.slug,
    );

    return {
      steps,
      currentStep,
      currentStepIndex,
      currentCategory,
      currentCategoryIndex,
      currentSection,
      currentSectionIndex,
      currentDisplayStepIndex,
      totalDisplaySteps,
      isClaimDigitised,
      claimId,
      isClaimPreDigitised,
      digitisationSuccessful,
      typeOfCare,
      insuranceProviderHandle,
      tpaName,
      typeOfBenefit,
      typeOfClaim,
    };
  }, [claimDetails, step, category, section]);

  const currentStepSlug = activeSteps?.currentStep?.slug;

  const prevStepRef = useRef<string | null>(currentStepSlug);

  // for starting and pausing the timer on step change
  useEffect(() => {
    if (currentStepSlug !== prevStepRef.current) {
      startTimer(currentStepSlug, true);
      if (prevStepRef.current) {
        pauseTimer(prevStepRef.current);
      }
      prevStepRef.current = currentStepSlug;
    }
  }, [currentStepSlug]);

  const navigate = useNavigate();
  const location = useLocation();

  // for resetting some of step-specific states on page change
  useEffect(() => {
    if (currentStepSlug) {
      setState((draft) => {
        draft.editInProgressRefs = new Map();
      });
    }
  }, [currentStepSlug]);

  useEffect(() => {
    if (claimDetails?.stage === EClaimStages.DOCUMENTS_SUBMITTED) {
      setState((draft) => {
        draft.isSubmitted = true;
      });
    }
  }, [claimDetails]);

  // for redirecting to /upload/:step pages
  useEffect(() => {
    if (autoRedirectToUpload && claimDetails != null) {
      window.dev.i('Redirecting to last allowed url...', claimDetails);
      (async () => {
        const { redirectTo, isAllowed, allowedUrls } = getLastAllowedUrl(
          claimDetails,
          step ?? '',
        );
        window.dev.i(
          'Redirecting to last allowed url...',
          redirectTo,
          isAllowed,
          allowedUrls,
        );
        if (!isAllowed) {
          navigate(allowedUrls[0]);
        }
        setState((draft) => {
          draft.isRouteLoading = false;
        });
      })();
    }
  }, [claimDetails, autoRedirectToUpload, location.pathname]);

  // for redirecting to /claims/[id] status page (when the upload is completed and user tries to navigate to /upload)
  useEffect(() => {
    if (autoRedirectToStatus) {
      (async () => {
        if (claimDetails?.typeOfClaim) {
          if (claimDetails?.typeOfClaim === EClaimType.REIMBURSEMENT) {
            // redirect if upload is done and submitted [reimbursement]
            if (
              claimDetails?.stage != null &&
              claimDetails?.stage !== 'documents-requested'
            ) {
              navigate(claimDetails.redirectTo);
            }
            setState((draft) => {
              draft.isRouteLoading = false;
            });
          }
        }
      })();
    }
  }, [
    autoRedirectToStatus,
    claimDetails?.stage,
    claimDetails?.redirectTo,
    claimDetails?.plumClaimId,
    claimDetails?.typeOfClaim,
  ]);

  useEffect(() => {
    const isHealthWalletClaimsV2Enabled = userConfig?.featureFlags.find(
      (flag) => flag.name === FEATURE_FLAGS.HEALTH_WALLET_CLAIMS_V2,
    )?.enabled;
    if (!isHealthWalletClaimsV2Enabled && !isAppLoading) {
      redirectToPlumApp(`/claims/v2/${claimId}/upload`)();
    }
  }, [userConfig, claimId, isAppLoading]);

  const { mutateAsync: fetchSignedUrl } = useMutation({
    ...generateSignedUrl({ claimId: claimDetails?.plumClaimId ?? '' }),
  });

  const { mutate: deleteDocument } = useMutation({
    ...deleteClaimDocument({ claimId: claimDetails?.plumClaimId ?? '' }),
  });

  const { Alert, triggerConfirm } = useConfirm();

  const onFileDelete = async (fileId: string) => {
    const isConfirmed = await triggerConfirm({
      title: 'Are you sure you want to delete this file?',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, Delete',
    });

    if (isConfirmed) {
      const newDocs = claimDetails?.documents?.filter(
        (doc) => doc.documentId !== fileId,
      );

      queryClient.setQueryData(
        claimDetailsQuery({
          claimId: claimDetails?.plumClaimId ?? '',
          returnDocumentsUploadPageContent: 3,
        }).queryKey,
        (data: IClaimDetails) => {
          if (!data) return data;
          return {
            ...data,
            documents: newDocs,
          };
        },
      );

      deleteDocument({ documentId: fileId });
    }
  };

  const onFileUpload = async (
    files: Array<File>,
    fileAttrs: { attrs: Record<string, unknown>; sectionTag: string },
  ) => {
    const promises: Array<ReturnType<typeof fetchSignedUrl>> = files.map(
      (file) => {
        return fetchSignedUrl({
          filename: file.name,
          sectionTag: fileAttrs.sectionTag,
          attrs: fileAttrs.attrs,
        });
      },
    );
    // run uploads in parallel
    const responses = await Promise.allSettled(promises);

    // optimistic update
    queryClient.setQueryData(
      claimDetailsQuery({
        claimId: claimDetails?.plumClaimId ?? '',
        returnDocumentsUploadPageContent: 3,
      }).queryKey,
      (data: IClaimDetails) => {
        if (!data) return data;
        return {
          ...data,
          documents: [
            ...(data?.documents ?? []),
            ...responses.map((response, index) => {
              if (response.status === 'fulfilled') {
                return {
                  documentId: response.value.documentId,
                  name: files?.[index]?.name,
                  sectionTag: fileAttrs.sectionTag,
                };
              }
            }),
          ],
        };
      },
    );

    responses.forEach((response, index) => {
      const file = files[index];
      if (response.status === 'fulfilled') {
        const signedUrl = response.value;
        if (signedUrl.error) {
          // log.error(
          //   "There is some issue in generating signed url: ",
          //   signedUrl.error
          // );
          return;
        }
        // optimistic update without revalidation
        // onUploadFile({
        //   ...signedUrl,
        //   name: file.name,
        //   sectionTag:
        //     sectionTag === EDocumentSectionTags.PATIENT_ID_PROOF_SECTION
        //       ? EDocumentSectionTags.ID_PROOF_SECTION
        //       : sectionTag,
        // });

        window.dev.i(
          'Uploading file...',
          signedUrl,
          file,
          state,
          state.uploadProgress,
        );
        setState((draft) => {
          draft.uploadProgress.set(signedUrl.documentId, 0.1);
        });

        uploadFile(signedUrl.url, file, {
          onError: () => {
            window.dev.i('Failed to upload the file.', signedUrl, file);
            setState((draft) => {
              draft?.uploadProgress?.delete(signedUrl.documentId);
            });
            toast.error('Failed to upload the file.');
          },
          onProgress: (_, uploadedPercentage) => {
            window.dev.i('uploadedPercentage', uploadedPercentage);
            setState((draft) => {
              draft.uploadProgress?.set(
                signedUrl.documentId,
                uploadedPercentage,
              );
            });
          },
          onUploadComplete: () => {
            window.dev.i('File uploaded successfully.', signedUrl, file);
            setState((draft) => {
              draft?.uploadProgress?.delete(signedUrl.documentId);
              // revalidate once all uploads are done
              if (draft.uploadProgress?.size === 0) {
                queryClient.invalidateQueries({
                  queryKey: ['claim-details', claimDetails?.plumClaimId],
                });
              }
            });
          },
        });
      }
    });
  };

  return (
    <ClaimUploadContext.Provider
      value={{
        getLastAllowedUrl,
        getTrackingStats: getTrackingStats(activeSteps, durationRef),
        state,
        setState,
        activeSteps,
        claimDetails,
        claimDetailsQueryResult,
        onFileDelete,
        onFileUpload,
        durationRef,
      }}
    >
      {children}
      <Alert />
    </ClaimUploadContext.Provider>
  );
};

export const useClaimUpload = () => {
  const context = useContext(ClaimUploadContext);
  if (!context)
    throw new Error('useClaimUpload must be used within a ClaimUploadProvider');
  return context;
};
